import { cn, Stack, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'component-library';
import { useTranslation } from 'react-i18next';

import { LandscapeType, PlotReportFactElement, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { GraphFact } from '@/components/Charts/types';
import { NCCardSectionHeader, printLatestDate } from '@/components/NCCard/NCCard';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { isNullish } from '@/utils/booleans';

export const LandCoverChangeStatsTile = ({
  landCoverChange,
}: {
  landCoverChange: PlotReportFactElement<GraphFact[]>;
}) => {
  const { t } = useTranslation();

  const noStatsData = isNullish(landCoverChange.value);

  if (noStatsData) {
    return null;
  }

  const statsData = landCoverChange.value.sort((a, b) => a.date.localeCompare(b.date));

  const latestAnalysisDate = printLatestDate([landCoverChange.measured_at]);
  return (
    <Stack className='gap-8 px-6 py-8 lg:gap-4 h-full w-full' data-testid='land-cover-change-stats-tile'>
      <NCCardSectionHeader
        title={<></>}
        supplementaryInfo={
          latestAnalysisDate ? `${t('shared.ncaDetail.details.labels.updated')} ${latestAnalysisDate}` : undefined
        }
      />
      <Stack className='m-auto p-4 w-full'>
        <LandscapeChangeExplainer statsData={statsData} />
      </Stack>
    </Stack>
  );
};

const LandscapeChangeExplainer = ({ statsData }: { statsData: GraphFact[] | undefined }) => {
  const { t } = useTranslation();

  if (!statsData || !statsData.length) {
    return (
      <p className='typography-body1 text-text-secondary'>
        {t('shared.ncaDetail.details.landCoverChange.stats.explainer.noData')}
      </p>
    );
  }

  const landscapeTypeChanges = Object.values(LandscapeType)
    .sort((a, b) => a.localeCompare(b))
    .flatMap((landscapeType) => {
      const landscapeTypeData = statsData.filter((entry) =>
        entry?.labels?.some(
          (label) => label.name === PlotReportFactLabelNameEnum.landscape_type && label.value === landscapeType,
        ),
      );

      if (landscapeTypeData.length <= 1) {
        return [];
      }

      const sortedLandscapeTypeData = landscapeTypeData.sort((a, b) => a.date.localeCompare(b.date));

      const startDataEntry = sortedLandscapeTypeData?.at(0);
      const endDataEntry = sortedLandscapeTypeData?.at(-1);

      const startDateTotalValue = statsData.reduce((acc, curr) => {
        return curr.date === startDataEntry?.date && typeof curr.value === 'number' ? acc + curr.value : acc;
      }, 0);
      const endDateTotalValue = statsData.reduce((acc, curr) => {
        return curr.date === endDataEntry?.date && typeof curr.value === 'number' ? acc + curr.value : acc;
      }, 0);

      if (
        !startDataEntry?.value ||
        !endDataEntry?.value ||
        !startDateTotalValue ||
        !endDateTotalValue ||
        startDataEntry.date === endDataEntry.date
      ) {
        return [];
      }

      const startValue = (startDataEntry.value / startDateTotalValue) * 100;
      const endValue = (endDataEntry.value / endDateTotalValue) * 100;

      if (startValue === null || endValue === null) {
        return [];
      }

      const changePct = endValue - startValue;
      const changePctLabel = getDisplayNumber(changePct, window.navigator.language, { signDisplay: 'exceptZero' });

      return [
        {
          landscapeType,
          landscapeTypeLabel: t(`global.landscapeTypes.${landscapeType}`),
          changePct: changePctLabel,
        },
      ];
    });

  return (
    <div className='max-h-[400px] overflow-y-auto w-full bg-[#fbfbfb] rounded-2xl py-2 px-4'>
      <Table data-testid='land-cover-change-table'>
        <TableHeader>
          <TableRow>
            <TableHead>{t('shared.ncaDetail.details.landCoverChange.stats.explainer.table.categoryHeader')}</TableHead>
            <TableHead>{t('shared.ncaDetail.details.landCoverChange.stats.explainer.table.changeHeader')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {landscapeTypeChanges.map(({ landscapeType, landscapeTypeLabel, changePct }) => (
            <TableRow key={landscapeType}>
              <TableCell>{landscapeTypeLabel}</TableCell>
              <TableCell
                className={cn({
                  'text-success': parseFloat(changePct) > 0,
                  'text-error': parseFloat(changePct) < 0,
                })}
              >
                {`${changePct}${t('global.ui.unit.%')}`}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
